import { AppIcon, type IconDefinition } from '@/components/ui/app-icon';
import {
  AgentCard,
  CARD_LINK,
} from '@/components/agent-chat/agent-card.component';
import {
  CardMeta,
  CardStatus,
} from '@/components/agent-chat/card-parts.component';
import { CompanyProfileCard } from '@/components/agent-chat/company-profile-card.component';
import {
  audienceSearchQuery,
  audienceSearchRefusal,
  useOpenAudienceInSearch,
} from '@/components/agent-chat/use-open-audience-in-search.hook';
import {
  GUMLOOP_TABLE,
  GUMLOOP_TABLE_SCROLL,
  GUMLOOP_TD,
  GUMLOOP_TH,
} from '@/components/agent-chat/gumloop-table';
import {
  iconBuilding,
  iconCircleCheck,
  iconGlobe,
  iconRobot,
  iconTable,
  iconTriangleExclamation,
  iconUsers,
} from '@/lib/icons';
import {
  ENRICHMENT_RUN_DISPLAY,
  SKILL_RUN_DISPLAY,
} from '@/components/agent-chat/run-progress/run-progress.util';
import {
  EnrichmentRunRow,
  SkillRunRow,
} from '@/components/agent-chat/run-progress/run-row.component';
import {
  isPhoneValidation,
  threadValidationCopyText,
  validationCopyText,
  validationRows,
} from '@/components/agent-chat/validation-results.util';
import { isInternalList } from '@/utils/internal-list.util';
import { Feature } from '@/constants/features';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { copyText } from '@/lib/clipboard';
import { cn } from '@/lib/utils';
import { useAuiState } from '@assistant-ui/react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';

/**
 * A tool's result in Clu's chat in the agents look, the same in every chat:
 * each sits in the agent card's shell (Gumloop's card, see AgentCard) with a
 * title and a state, and a table reads as a table Clu writes in its answer
 * (see gumloop-table). No timeline line above it: the steps line names the
 * step, and a result that failed draws no card (its step says why).
 */

/** The small link a card's header ends in: the card standard's link. */
export const RESULT_CARD_LINK = CARD_LINK;

/** A header's right side: a status, a count, its links. */
function HeaderEnd({ children }: { children: ReactNode }) {
  return (
    <span className='flex items-center gap-[8px] pr-[4px] text-[12px] leading-[16px] text-muted-foreground'>
      {children}
    </span>
  );
}

/** The header's icon tile, in the danger colour for an error. */
export function IconTile({
  icon,
  Icon,
  tone,
}: {
  icon?: IconDefinition;
  Icon?: ComponentType<{ className?: string }>;
  tone?: 'error';
}) {
  return (
    <div
      className={cn(
        'flex size-[20px] flex-none items-center justify-center rounded-[6px] border border-border bg-background text-muted-foreground',
        tone === 'error' && 'text-destructive'
      )}
    >
      {icon ? <AppIcon icon={icon} style={{ fontSize: 10 }} /> : null}
      {!icon && Icon ? <Icon className='size-[10px]' /> : null}
    </div>
  );
}

/** A result in the shell: the icon, the title, anything trailing, the body. */
export function ResultCard({
  icon,
  Icon,
  title,
  trailing,
  tone,
  children,
  ...rest
}: {
  icon?: IconDefinition;
  /** An icon component instead (the old surfaces' icons). */
  Icon?: ComponentType<{ className?: string }>;
  title: ReactNode;
  trailing?: ReactNode;
  tone?: 'error';
  children?: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, 'title'>) {
  return (
    <AgentCard
      data-result-card
      leading={<IconTile icon={icon} Icon={Icon} tone={tone} />}
      title={title}
      trailing={trailing}
      bodyClassName='p-0 gap-0 overflow-hidden'
      {...rest}
    >
      {children}
    </AgentCard>
  );
}

export interface ResultColumn {
  key: string;
  label: string;
}

/** A result's rows as the answer's table, the body of a result card. */
export function ResultTable({
  columns,
  rows,
}: {
  columns: ResultColumn[];
  rows: Array<{ id: string; cells: Record<string, ReactNode> }>;
}) {
  return (
    <div className='max-h-[360px] overflow-y-auto p-[12px]'>
      <div className={GUMLOOP_TABLE_SCROLL}>
        <table className={GUMLOOP_TABLE} data-result-table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className={GUMLOOP_TH}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                {columns.map((column) => (
                  <td key={column.key} className={GUMLOOP_TD}>
                    {row.cells[column.key] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function counted(count: number, one: string, many: string): string {
  return `${count.toLocaleString()} ${count === 1 ? one : many}`;
}

function asRecords(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is Record<string, unknown> =>
          !!item && typeof item === 'object' && !Array.isArray(item)
      )
    : [];
}

function textOf(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : '';
}

function safeHttp(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  const withScheme = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const parsed = new URL(withScheme);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
      ? parsed.toString()
      : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// People, lists, agents, validation, a company
// ---------------------------------------------------------------------------

export interface PersonRow {
  id: string;
  name: string;
  title: string;
  company: string;
  location: string;
  linkedin_url: string;
}

/** People already in a list, or a search nothing on the page could take. */
export function PeopleResultCard({
  people,
  total,
}: {
  people: PersonRow[];
  total: number;
}) {
  const shown = people.length;
  return (
    <ResultCard
      icon={iconUsers}
      title={
        total > shown
          ? `${shown.toLocaleString()} of ${counted(total, 'person', 'people')}`
          : counted(shown, 'person', 'people')
      }
    >
      <ResultTable
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'title', label: 'Title' },
          { key: 'company', label: 'Company' },
          { key: 'location', label: 'Location' },
        ]}
        rows={people.map((person) => {
          const href = safeHttp(person.linkedin_url);
          return {
            id: person.id,
            cells: {
              name: href ? (
                <a
                  href={href}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='font-medium underline decoration-border underline-offset-[3px] hover:decoration-foreground'
                >
                  {person.name}
                </a>
              ) : (
                person.name
              ),
              title: person.title,
              company: person.company,
              location: person.location,
            },
          };
        })}
      />
    </ResultCard>
  );
}

/** The organization's lead lists, each opening its list. */
export function ListsResultCard({ lists }: { lists: unknown }) {
  const rows = asRecords(lists).filter((list) => !isInternalList(list));
  if (rows.length === 0)
    return <ResultCard icon={iconTable} title='No lists' />;
  return (
    <ResultCard
      icon={iconTable}
      title={counted(rows.length, 'list', 'lists')}
      trailing={
        <HeaderEnd>
          <Link to='/lists' className={CARD_LINK}>
            View all
          </Link>
        </HeaderEnd>
      }
    >
      <ResultTable
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'leads', label: 'Leads' },
        ]}
        rows={rows.map((list, index) => {
          const id = textOf(list.id);
          const name = textOf(list.name) || 'Untitled';
          const leads = Number(list.lead_count ?? 0);
          return {
            id: id || String(index),
            cells: {
              name: id ? (
                <Link
                  to='/lists/$listId'
                  params={{ listId: id }}
                  className='font-medium underline decoration-border underline-offset-[3px] hover:decoration-foreground'
                >
                  {name}
                </Link>
              ) : (
                name
              ),
              leads: Number.isFinite(leads) ? leads.toLocaleString() : '0',
            },
          };
        })}
      />
    </ResultCard>
  );
}

/**
 * The organization's saved agents. View all opens the Agents page, drawn only
 * where that page opens (on Free it is a conversion page).
 */
export function AgentsResultCard({ agents }: { agents: unknown }) {
  const { hasFeature } = useFeatureAccess();
  const rows = asRecords(agents);
  if (rows.length === 0) {
    return <ResultCard icon={iconRobot} title='No saved agents' />;
  }
  return (
    <ResultCard
      icon={iconRobot}
      title={counted(rows.length, 'agent', 'agents')}
      trailing={
        hasFeature(Feature.AGENTS) ? (
          <HeaderEnd>
            <Link to='/agents' search={{ tab: 'agents' }} className={CARD_LINK}>
              View all
            </Link>
          </HeaderEnd>
        ) : undefined
      }
    >
      <ResultTable
        columns={[
          { key: 'name', label: 'Agent' },
          { key: 'description', label: 'Description' },
        ]}
        rows={rows.map((agent, index) => ({
          id: textOf(agent.id) || String(index),
          cells: {
            name: textOf(agent.name) || 'Untitled',
            description: textOf(agent.description),
          },
        }))}
      />
    </ResultCard>
  );
}

/** A verdict's color: deliverable, risky, undeliverable, unknown. */
function verdictClass(verdict: string, ok: boolean): string {
  const value = verdict.toLowerCase();
  if (ok || value === 'deliverable' || value === 'valid') {
    return 'text-emerald-600 dark:text-emerald-400';
  }
  if (value === 'risky') return 'text-amber-600 dark:text-amber-400';
  if (value === 'undeliverable' || value === 'invalid') {
    return 'text-destructive';
  }
  return 'text-muted-foreground';
}

/** A header link that copies `value`, saying Copied for a moment after. */
function CopyLink({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copied]);
  const copy = async () => {
    if (await copyText(value)) setCopied(true);
    else toast.error("Couldn't copy. Select the text and copy it instead.");
  };
  return (
    <button type='button' className={CARD_LINK} onClick={() => void copy()}>
      {copied ? 'Copied' : label}
    </button>
  );
}

/**
 * One-off email or phone validation: each value and its result, a phone's
 * line type and carrier too. Copy writes the rows one line a value; on the
 * newest of several checks, Copy all writes every check in the chat.
 */
export function ValidationResultCard({ result }: { result: unknown }) {
  const data = (result ?? {}) as Record<string, unknown>;
  const rows = validationRows(data);
  const valid = Number(data.valid_count ?? 0) || 0;
  const total = Number(data.total ?? 0) || rows.length;
  const phone = isPhoneValidation(data);
  const copyAll = useAuiState((state) =>
    threadValidationCopyText(state.thread.messages, result)
  );
  const copyAllCount = copyAll ? copyAll.split('\n').length : 0;
  return (
    <ResultCard
      icon={iconCircleCheck}
      title={phone ? 'Phone check' : 'Email check'}
      trailing={
        <HeaderEnd>
          <CardStatus>
            {valid.toLocaleString()} of {total.toLocaleString()} good
          </CardStatus>
          {rows.length > 0 ? (
            <CopyLink value={validationCopyText(data)} label='Copy' />
          ) : null}
          {copyAllCount > 0 ? (
            <CopyLink
              value={copyAll}
              label={`Copy all ${copyAllCount.toLocaleString()}`}
            />
          ) : null}
        </HeaderEnd>
      }
    >
      {rows.length > 0 ? (
        <ResultTable
          columns={[
            { key: 'value', label: phone ? 'Phone' : 'Email' },
            { key: 'verdict', label: 'Result' },
            ...(phone
              ? [
                  { key: 'lineType', label: 'Type' },
                  { key: 'carrier', label: 'Carrier' },
                ]
              : []),
          ]}
          rows={rows.map((row, index) => ({
            id: `${index}`,
            cells: {
              value: row.value,
              verdict: (
                <span
                  className={cn(
                    'font-medium capitalize',
                    verdictClass(row.verdict, row.ok)
                  )}
                >
                  {row.verdict}
                </span>
              ),
              lineType: row.lineType,
              carrier: row.carrier,
            },
          }))}
        />
      ) : null}
      {data.truncated === true ? (
        <CardMeta className='px-[12px] pb-[12px]'>
          Checked the first {rows.length.toLocaleString()}. Send the rest in
          another check.
        </CardMeta>
      ) : null}
    </ResultCard>
  );
}

const COMPANY_FIELDS: Array<[string, string]> = [
  ['domain', 'Domain'],
  ['industry', 'Industry'],
  ['size', 'Size'],
  ['location', 'Location'],
];

/** A company's profile: its name, then one row a field. */
export function CompanyResultCard({ result }: { result: unknown }) {
  const data = (result ?? {}) as Record<string, unknown>;
  const fields = COMPANY_FIELDS.filter(([key]) => textOf(data[key]));
  return (
    <ResultCard icon={iconBuilding} title={textOf(data.name) || 'Company'}>
      {fields.length > 0 ? (
        <div className='p-[12px]'>
          <div className={GUMLOOP_TABLE_SCROLL}>
            <table className={GUMLOOP_TABLE} data-result-table>
              <tbody>
                {fields.map(([key, label]) => (
                  <tr key={key}>
                    <th scope='row' className={cn(GUMLOOP_TH, 'w-[120px]')}>
                      {label}
                    </th>
                    <td className={GUMLOOP_TD}>{textOf(data[key])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </ResultCard>
  );
}

// ---------------------------------------------------------------------------
// Notices: a list made, leads moved, a page opened, a search handed off
// ---------------------------------------------------------------------------

function withoutArrow(children: ReactNode): ReactNode {
  return Children.map(children, (child) =>
    typeof child === 'string' ? child.replace(/\s*→\s*$/, '') : child
  );
}

/** A plain link or button, restyled as the card's link (its own classes,
 * as an export's `text-xs text-primary`, give way). */
function asCardLink(node: ReactNode): ReactElement | null {
  if (!isValidElement(node)) return null;
  if (node.type !== 'button' && node.type !== 'a') return null;
  const props = node.props as { children?: ReactNode };
  return cloneElement(node as ReactElement<Record<string, unknown>>, {
    className: CARD_LINK,
    children: withoutArrow(props.children),
  });
}

/**
 * The action in an old notice's description (View results, View list, Go
 * again), restyled as the card's link. A description that is only words is
 * helper text, which the card leaves out.
 */
function actionOf(node: ReactNode): ReactElement | null {
  if (!isValidElement(node)) return null;
  const own = asCardLink(node);
  if (own) return own;
  const props = node.props as { children?: ReactNode };
  for (const child of Children.toArray(props.children)) {
    const found = actionOf(child);
    if (found) return found;
  }
  return null;
}

/** An old notice (ResultNotice) in the agents look: title and action only. */
export function ResultNoticeCard({
  Icon,
  title,
  description,
  action,
  trailing,
  variant,
}: {
  Icon: ComponentType<{ className?: string }>;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  trailing?: ReactNode;
  variant?: 'success' | 'error' | 'neutral';
}) {
  const link = action ? (asCardLink(action) ?? action) : actionOf(description);
  const end =
    trailing || link ? (
      <HeaderEnd>
        {trailing}
        {link}
      </HeaderEnd>
    ) : undefined;
  return (
    <ResultCard
      Icon={variant === 'error' ? undefined : Icon}
      icon={variant === 'error' ? iconTriangleExclamation : undefined}
      tone={variant === 'error' ? 'error' : undefined}
      title={title}
      trailing={end}
    />
  );
}

/** A web search's people, their cards kept whole inside the shell. */
export function WebPeopleResultCard({
  count,
  children,
}: {
  count: number;
  children: ReactNode;
}) {
  return (
    <ResultCard icon={iconGlobe} title={counted(count, 'person', 'people')}>
      <div className='p-[12px]'>{children}</div>
    </ResultCard>
  );
}

/**
 * The saved company profile, its audience opening in Find People and Edit
 * opening it on the Agents page. Edit is drawn only where that page opens:
 * on Free it is a conversion page, and the way to change the profile there is
 * to tell Clu.
 */
function CompanyProfileResult({ result }: { result: Record<string, unknown> }) {
  const openInSearch = useOpenAudienceInSearch();
  const navigate = useNavigate();
  const { hasFeature } = useFeatureAccess();
  const q = useMemo(() => audienceSearchQuery(result.search), [result.search]);
  const searchRefusal = useMemo(
    () => audienceSearchRefusal(result.search),
    [result.search]
  );
  const canEdit = hasFeature(Feature.AGENTS);
  return (
    <CompanyProfileCard
      result={result}
      onEdit={
        canEdit
          ? () => void navigate({ to: '/agents', search: { tab: 'profile' } })
          : undefined
      }
      onOpenSearch={q ? () => openInSearch(q) : undefined}
      searchRefusal={searchRefusal}
    />
  );
}

/**
 * A result in the agents look, by its display. Tables and a company draw
 * here; the rest draw their own surface, which takes the look itself
 * (ResultNotice, the list notice and the people table read it), so a handoff
 * still opens its page and a run still reports its progress.
 */
export function GumloopResultCard({
  displayType,
  toolName,
  result,
  mirrored,
  legacy,
}: {
  displayType: string;
  toolName: string;
  result: Record<string, unknown>;
  /** A people result the home Workspace pane shows beside the chat. */
  mirrored: boolean;
  legacy: () => ReactNode;
}) {
  if (mirrored) {
    const count = Array.isArray(result.results) ? result.results.length : 0;
    return (
      <ResultCard
        icon={iconUsers}
        title={
          count > 0
            ? `${counted(count, 'person', 'people')} in the Workspace`
            : 'In the Workspace'
        }
      />
    );
  }
  switch (displayType) {
    case 'lead_lists':
      return <ListsResultCard lists={result.lists} />;
    case 'saved_agents':
      return <AgentsResultCard agents={result.agents} />;
    case 'validation_results':
      return <ValidationResultCard result={result} />;
    case 'company_info':
      return <CompanyResultCard result={result} />;
    case 'company_profile':
      return <CompanyProfileResult result={result} />;
    // A run reads as one row, the size of a step: no card.
    case ENRICHMENT_RUN_DISPLAY:
      return <EnrichmentRunRow result={result} />;
    case SKILL_RUN_DISPLAY:
      return <SkillRunRow result={result} />;
    case 'people_table': {
      const count = Array.isArray(result.results) ? result.results.length : 0;
      if (toolName === 'web_people_search' && count > 0) {
        return (
          <WebPeopleResultCard count={count}>{legacy()}</WebPeopleResultCard>
        );
      }
      return <>{legacy()}</>;
    }
    default:
      return <>{legacy()}</>;
  }
}
